// Complete email templates with block structures
//
// Design language (editorial, inspired by premium email design):
// - Typography-led heroes: large serif (Georgia) or heavy caps display type
// - Tiny letterspaced micro-caps for eyebrows and utility bars
// - Outlined or solid pill buttons, never default rounded rectangles
// - Colour-blocked sections with generous padding, no gradients
// - Muted sophisticated palettes, one palette per template
export const emailTemplates = [
  // ── Grand Opening — minimal black & white ──
  {
    id: 'grand-opening',
    name: 'Grand Opening',
    description: 'Make a stunning first impression with a clean, editorial showcase of your new space and services.',
    category: 'Announcement',
    subject: 'We\'re Open! Come See the New {{SalonName}}',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'A new destination',
        rightText: 'Now open',
        textColor: '#1a1a1a',
        backgroundColor: '#ffffff'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'WE\'RE\nOPEN',
        subtext: 'A new destination for beauty',
        backgroundColor: '#ffffff',
        textColor: '#1a1a1a',
        headlineFontFamily: 'Inter, system-ui, sans-serif',
        headlineWeight: '800',
        headlineUppercase: true,
        headlineSize: 52
      }},
      { id: 'img-1', type: 'image', content: {
        url: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800',
        fullWidth: true
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        heading: 'A space designed around you',
        body: 'Hi {{FirstName}}, we\'re thrilled to open our doors and welcome you to a space built entirely around your comfort and confidence.',
        textColor: '#1a1a1a',
        backgroundColor: '#ffffff'
      }},
      { id: 'btn-1', type: 'button', content: {
        text: 'Book your first visit',
        url: '{{BookingLink}}',
        variant: 'outline',
        shape: 'pill',
        uppercase: true,
        fontSize: 12,
        buttonColor: '#1a1a1a',
        align: 'center'
      }},
      { id: 'duo-1', type: 'layout-duo-grid', content: {
        backgroundColor: '#f6f4f1',
        textColor: '#1a1a1a',
        leftImage: 'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=600',
        leftTitle: 'The space',
        leftCaption: 'Calm, considered, and made for switching off.',
        rightImage: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=600',
        rightTitle: 'The craft',
        rightCaption: 'A team obsessed with the details.'
      }},
      { id: 'intro-2', type: 'layout-section-intro', content: {
        eyebrow: 'Signature services',
        heading: 'Where to begin',
        accentColor: '#9ca3af',
        textColor: '#1a1a1a',
        backgroundColor: '#ffffff'
      }},
      { id: 'service-1', type: 'service', content: {
        name: 'Hydra-Glow Facial',
        description: 'Deep hydration meets luminous results. Our signature treatment for radiant, refreshed skin.',
        price: '120',
        duration: '60 min',
        image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400',
        backgroundColor: '#f6f4f1',
        priceColor: '#1a1a1a'
      }},
      { id: 'service-2', type: 'service', content: {
        name: 'Balayage & Blow Dry',
        description: 'Hand-painted colour that looks effortlessly natural. Includes luxury blow dry finish.',
        price: '180',
        duration: '2.5 hrs',
        image: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=400',
        backgroundColor: '#f6f4f1',
        priceColor: '#1a1a1a'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        eyebrow: 'To celebrate',
        headline: 'Opening week, 15% off any service',
        buttonText: 'Book now',
        buttonUrl: '{{BookingLink}}',
        background: '#29282D',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonColor: '#ffffff',
        buttonTextColor: '#29282D',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'social-1', type: 'layout-social-strip', content: { style: 'rounded' }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Before & After Showcase — warm beige & olive gold ──
  {
    id: 'before-after-showcase',
    name: 'Before & After',
    description: 'Highlight dramatic transformations with side-by-side comparisons and authentic client reviews.',
    category: 'Newsletter',
    subject: 'See the Transformations ✨',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'Real clients',
        rightText: 'Real results',
        textColor: '#8a7b4f',
        backgroundColor: '#f7f2ec'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'Nice to see\na new you',
        subtext: 'Nothing speaks louder than real results. Here are a few of our favourite recent transformations.',
        backgroundColor: '#f7f2ec',
        textColor: '#6b5e3a',
        headlineFontFamily: 'Georgia, serif',
        headlineItalic: true,
        headlineSize: 42
      }},
      { id: 'text-1', type: 'text', content: {
        text: 'Hi {{FirstName}}, every one of these began with a consultation and a conversation.',
        align: 'center',
        fontSize: '15',
        textColor: '#6b7280'
      }},
      { id: 'ba-1', type: 'layout-before-after', content: {
        beforeLabel: 'Before',
        afterLabel: 'After',
        beforeImage: 'https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=400',
        afterImage: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=400'
      }},
      { id: 'review-1', type: 'review', content: {
        text: 'I walked in feeling dull and walked out absolutely glowing. Sarah completely transformed my hair and I\'ve never felt more confident!',
        author: 'Jessica M.',
        service: 'Balayage Highlights',
        rating: 5,
        backgroundColor: '#f7f2ec'
      }},
      { id: 'ba-2', type: 'layout-before-after', content: {
        beforeLabel: 'Before',
        afterLabel: 'After',
        beforeImage: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=400',
        afterImage: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400'
      }},
      { id: 'review-2', type: 'review', content: {
        text: 'My skin has never looked this good. The facial was so relaxing and the results lasted for weeks!',
        author: 'Amanda R.',
        service: 'Hydra-Glow Facial',
        rating: 5,
        backgroundColor: '#f7f2ec'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Your turn in the chair',
        subtext: 'Book a consultation and let\'s talk about what\'s possible',
        buttonText: 'Book a consultation',
        buttonUrl: '{{BookingLink}}',
        background: '#f7f2ec',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        headlineColor: '#6b5e3a',
        buttonVariant: 'outline',
        buttonColor: '#8a7b4f',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Client Results Showcase — minimal white & powder blue ──
  {
    id: 'client-results',
    name: 'Client Results',
    description: 'Feature real client stories with before/after photos, reviews, and the services that made it happen.',
    category: 'Newsletter',
    subject: 'Real Results. Real Clients.',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'The proof',
        rightText: 'Is in the chair',
        textColor: '#1a1a1a',
        backgroundColor: '#ffffff'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'REAL RESULTS.\nREAL CLIENTS.',
        subtext: 'See what our team can do for you',
        backgroundColor: '#ffffff',
        textColor: '#1a1a1a',
        headlineFontFamily: 'Inter, system-ui, sans-serif',
        headlineWeight: '800',
        headlineUppercase: true,
        headlineSize: 40
      }},
      { id: 'img-1', type: 'image', content: {
        url: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800',
        fullWidth: true
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        heading: 'Our clients are our best ambassadors',
        body: 'Hi {{FirstName}}, here\'s a look at some incredible recent transformations and the stories behind them.',
        textColor: '#1a1a1a',
        backgroundColor: '#ffffff'
      }},
      { id: 'duo-1', type: 'layout-duo-grid', content: {
        backgroundColor: '#a9c2e1',
        textColor: '#16304f',
        leftImage: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=600',
        leftTitle: 'Colour correction',
        leftCaption: 'Years of box dye, undone in one patient afternoon.',
        rightImage: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600',
        rightTitle: 'Skin transformation',
        rightCaption: 'Three sessions to a foundation-free morning.'
      }},
      { id: 'review-1', type: 'review', content: {
        text: 'After years of damage from box dye, I finally have healthy, gorgeous hair again. The colour correction took patience but the result is beyond what I imagined.',
        author: 'Olivia P.',
        service: 'Colour Correction',
        rating: 5,
        backgroundColor: '#f4f7fb'
      }},
      { id: 'ba-1', type: 'layout-before-after', content: {
        beforeLabel: 'Before',
        afterLabel: 'After',
        beforeImage: 'https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=400',
        afterImage: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=400'
      }},
      { id: 'review-2', type: 'review', content: {
        text: 'Three sessions in and my skin has completely transformed. I no longer wear foundation, and that says it all!',
        author: 'Priya K.',
        service: 'Advanced Facial Course',
        rating: 5,
        backgroundColor: '#f4f7fb'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Start your transformation',
        subtext: 'Free consultation for first-time clients',
        buttonText: 'Book a consultation',
        buttonUrl: '{{BookingLink}}',
        background: '#a9c2e1',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        headlineColor: '#16304f',
        buttonColor: '#16304f',
        buttonTextColor: '#ffffff',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Seasonal Service Menu — warm cream & olive ──
  {
    id: 'seasonal-menu',
    name: 'Seasonal Menu',
    description: 'Showcase your seasonal treatments with a curated service menu, client review, and location details.',
    category: 'Promotional',
    subject: '🌿 New Season, New Treatments',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'The seasonal edit',
        rightText: 'New season',
        textColor: '#7c7350',
        backgroundColor: '#f6f1e7'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        eyebrow: 'This season\'s treatments',
        headline: 'The Spring Menu',
        subtext: 'A curated collection of seasonal treatments, designed to refresh and rejuvenate as the season changes.',
        backgroundColor: '#f6f1e7',
        textColor: '#5c5436',
        accentColor: '#7c7350',
        headlineFontFamily: 'Georgia, serif',
        headlineSize: 42
      }},
      { id: 'img-1', type: 'image', content: {
        url: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=800',
        fullWidth: true
      }},
      { id: 'service-1', type: 'service', content: {
        name: 'Spring Glow Facial',
        description: 'Brighten dull winter skin with vitamin C, gentle exfoliation, and our botanical hydration mask.',
        price: '95',
        duration: '50 min',
        image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400',
        backgroundColor: '#faf7f0',
        priceColor: '#7c7350'
      }},
      { id: 'service-2', type: 'service', content: {
        name: 'Botanical Hair Treatment',
        description: 'Deep repair with plant-based proteins. Restore shine and strength after cold weather damage.',
        price: '75',
        duration: '40 min',
        image: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=400',
        backgroundColor: '#faf7f0',
        priceColor: '#7c7350'
      }},
      { id: 'service-3', type: 'service', content: {
        name: 'Fresh Cut & Blow Dry',
        description: 'New season, new shape. Precision cut with a polished blow dry finish.',
        price: '65',
        duration: '45 min',
        backgroundColor: '#faf7f0',
        priceColor: '#7c7350'
      }},
      { id: 'service-4', type: 'service', content: {
        name: 'Lash Lift & Tint',
        description: 'Wake up ready with perfectly curled, darkened lashes. Results last 6-8 weeks.',
        price: '55',
        duration: '45 min',
        backgroundColor: '#faf7f0',
        priceColor: '#7c7350'
      }},
      { id: 'promo-1', type: 'layout-promo-banner', content: {
        promoText: 'SPRING BUNDLE, 20% OFF',
        subtext: 'Book any two seasonal treatments together',
        backgroundColor: '#7c7350',
        promoTextColor: '#ffffff',
        subtextColor: '#e5dfc8'
      }},
      { id: 'testimonial-1', type: 'layout-testimonial', content: {
        quote: 'The spring facial was exactly what my skin needed. I left looking and feeling completely refreshed, and I have already booked my next one!',
        name: 'Sophie L.',
        title: 'Regular Client',
        textColor: '#5c5436',
        backgroundColor: '#f6f1e7'
      }},
      { id: 'btn-1', type: 'button', content: {
        text: 'Browse the full menu',
        url: '{{BookingLink}}',
        variant: 'outline',
        shape: 'pill',
        uppercase: true,
        fontSize: 12,
        buttonColor: '#7c7350',
        align: 'center'
      }},
      { id: 'contact-1', type: 'layout-contact-card', content: {
        businessName: 'Your Salon',
        address: '123 High Street',
        phone: '020 1234 5678',
        hours: 'Tue to Sat, 9am to 7pm',
        email: 'hello@yoursalon.com',
        backgroundColor: '#faf7f0'
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Client Appreciation — soft blush & terracotta ──
  {
    id: 'client-appreciation',
    name: 'Client Appreciation',
    description: 'Thank your clients with heartfelt reviews and an exclusive limited-time reward.',
    category: 'VIP',
    subject: 'Thank You, and a Little Gift Inside',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'A note of thanks',
        textColor: '#b5715f',
        backgroundColor: '#f6ebe7'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'Thank you,\ntruly.',
        subtext: 'For being part of our story',
        backgroundColor: '#f6ebe7',
        textColor: '#8a5b4d',
        headlineFontFamily: 'Georgia, serif',
        headlineItalic: true,
        headlineSize: 44
      }},
      { id: 'text-1', type: 'text', content: {
        text: 'Hi {{FirstName}},\n\nYou\'re not just a client, you\'re part of our family. We wanted to take a moment to say thank you, and share some kind words from people just like you.',
        align: 'center',
        fontSize: '15',
        textColor: '#6b7280'
      }},
      { id: 'review-1', type: 'review', content: {
        text: 'I\'ve been coming here for two years and every single visit exceeds expectations. The team genuinely cares about making you feel special.',
        author: 'Rebecca T.',
        service: 'Loyal Client',
        rating: 5,
        backgroundColor: '#fbf6f3'
      }},
      { id: 'review-2', type: 'review', content: {
        text: 'From the warm welcome to the incredible results, this salon is in a league of its own. I recommend it to everyone I know.',
        author: 'David H.',
        service: 'Signature Haircut',
        rating: 5,
        backgroundColor: '#fbf6f3'
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        eyebrow: 'As a thank you',
        heading: 'A little gift, just for you',
        accentColor: '#b5715f',
        textColor: '#4a3630',
        backgroundColor: '#ffffff'
      }},
      { id: 'feature-1', type: 'layout-feature-card', content: {
        headline: 'Your gift: 20% off',
        body: 'Use code THANKYOU20 on your next booking. Valid for any service, any stylist. Because you deserve it.',
        imageUrl: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600',
        textColor: '#4a3630',
        backgroundColor: '#ffffff'
      }},
      { id: 'countdown-1', type: 'layout-countdown', content: {
        headline: 'Your exclusive offer expires in',
        days: '02',
        hours: '14',
        mins: '30',
        secs: '00',
        backgroundColor: '#4a3630',
        textColor: '#ffffff'
      }},
      { id: 'btn-1', type: 'button', content: {
        text: 'Claim your gift',
        url: '{{BookingLink}}',
        shape: 'pill',
        uppercase: true,
        fontSize: 12,
        buttonColor: '#b5715f',
        textColor: '#ffffff',
        align: 'center'
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Meet the Team — minimal editorial black & white ──
  {
    id: 'meet-the-team',
    name: 'Meet the Team',
    description: 'Introduce your talented team with profiles, client reviews, and impressive stats.',
    category: 'Newsletter',
    subject: 'The People Behind Your Perfect Look',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'The people',
        rightText: 'Behind the look',
        textColor: '#161616',
        backgroundColor: '#ffffff'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'Meet the team',
        subtext: 'The talented artists behind every transformation. Get to know the people who make it all happen.',
        backgroundColor: '#ffffff',
        textColor: '#161616',
        headlineFontFamily: 'Georgia, serif',
        headlineSize: 44
      }},
      { id: 'img-1', type: 'image', content: {
        url: 'https://images.unsplash.com/photo-1521590832167-7bcbfaa6381f?w=800',
        fullWidth: true
      }},
      { id: 'team-1', type: 'team', content: {
        name: 'Sarah Mitchell',
        title: 'Creative Director & Colourist',
        bio: '12 years of experience specialising in balayage and lived-in colour. Trained at Sassoon Academy London.',
        image: 'https://images.unsplash.com/photo-1580618672591-eb180b1a973f?w=400',
        textColor: '#161616',
        accentColor: '#9c9489',
        ringColor: '#e8e4de'
      }},
      { id: 'team-2', type: 'team', content: {
        name: 'James Chen',
        title: 'Senior Stylist',
        bio: 'Precision cutting expert with a passion for creating shapes that work with your natural texture and lifestyle.',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400',
        textColor: '#161616',
        accentColor: '#9c9489',
        ringColor: '#e8e4de'
      }},
      { id: 'team-3', type: 'team', content: {
        name: 'Amara Obi',
        title: 'Skin Specialist',
        bio: 'Advanced aesthetics practitioner specialising in facial treatments. Your skin\'s new best friend.',
        image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400',
        textColor: '#161616',
        accentColor: '#9c9489',
        ringColor: '#e8e4de'
      }},
      { id: 'testimonial-1', type: 'layout-testimonial', content: {
        quote: 'Every single person on this team is incredible at what they do. I\'ve never had a bad experience, they make you feel like the only client in the room.',
        name: 'Charlotte D.',
        title: 'Client since 2021',
        textColor: '#161616',
        backgroundColor: '#f5f3f0'
      }},
      { id: 'stats-1', type: 'layout-stats', content: {
        stats: [
          { value: '2,500+', label: 'Happy Clients' },
          { value: '15+', label: 'Years Combined' },
          { value: '4.9', label: 'Average Rating' }
        ],
        textColor: '#161616'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Book with your favourite',
        subtext: 'Choose your team member when you book',
        buttonText: 'Book now',
        buttonUrl: '{{BookingLink}}',
        background: '#161616',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonColor: '#ffffff',
        buttonTextColor: '#161616',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Holiday / Event Promo — charcoal & champagne gold ──
  {
    id: 'holiday-event',
    name: 'Holiday Event',
    description: 'Create urgency and excitement with a holiday-themed campaign featuring limited edition services.',
    category: 'Promotional',
    subject: 'Holiday Specials, Limited Availability',
    blocks: [
      { id: 'hero-1', type: 'layout-full-bleed', content: {
        headline: 'The Holiday Edit',
        subtext: 'Limited edition treatments, here until the new year',
        imageUrl: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800',
        overlayOpacity: 50,
        textColor: '#ffffff',
        fontFamily: 'Georgia, serif'
      }},
      { id: 'badge-1', type: 'layout-badge', content: {
        badgeText: 'LIMITED EDITION',
        headline: 'Our holiday collection',
        body: 'These exclusive seasonal treatments are only available through December. Once they\'re gone, they\'re gone.',
        badgeColor: '#c9a35c',
        textColor: '#232026',
        backgroundColor: '#f8f4ec',
        fontFamily: 'Georgia, serif'
      }},
      { id: 'service-1', type: 'service', content: {
        name: 'Festive Blow Dry & Styling',
        description: 'Hollywood waves, sleek updo, or voluminous bounce, perfect for any party.',
        price: '55',
        duration: '45 min',
        backgroundColor: '#faf8f3',
        priceColor: '#a2823f'
      }},
      { id: 'service-2', type: 'service', content: {
        name: 'Winter Repair Treatment',
        description: 'Intensive moisture and bond repair for hair damaged by cold, dry weather.',
        price: '85',
        duration: '60 min',
        image: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=400',
        backgroundColor: '#faf8f3',
        priceColor: '#a2823f'
      }},
      { id: 'countdown-1', type: 'layout-countdown', content: {
        headline: 'Holiday bookings close in',
        days: '05',
        hours: '18',
        mins: '45',
        secs: '00',
        backgroundColor: '#232026',
        textColor: '#ffffff'
      }},
      { id: 'promo-1', type: 'layout-promo-banner', content: {
        promoText: 'GIFT CARDS AVAILABLE',
        subtext: 'The perfect present, buy online instantly',
        backgroundColor: '#c9a35c',
        promoTextColor: '#2b2417',
        subtextColor: '#4a3f28'
      }},
      { id: 'btn-1', type: 'button', content: {
        text: 'Book holiday appointments',
        url: '{{BookingLink}}',
        shape: 'pill',
        uppercase: true,
        fontSize: 12,
        buttonColor: '#232026',
        textColor: '#ffffff',
        align: 'center'
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Referral Program — colour-blocked sage ──
  {
    id: 'referral-program',
    name: 'Referral Program',
    description: 'Drive word-of-mouth growth with a referral rewards campaign featuring stats and social proof.',
    category: 'Promotional',
    subject: 'Give £20, Get £20 and Share the Love',
    blocks: [
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        eyebrow: 'Share the love',
        headline: 'Give £20.\nGet £20.',
        subtext: 'Refer a friend and you\'ll both receive £20 off your next appointment. It\'s our way of saying thanks for spreading the word.',
        backgroundColor: '#8f9e83',
        textColor: '#ffffff',
        accentColor: '#e8eddf',
        headlineFontFamily: 'Georgia, serif',
        headlineSize: 46
      }},
      { id: 'text-1', type: 'text', content: {
        text: 'Hi {{FirstName}}, know someone who deserves a little pampering?',
        align: 'center',
        fontSize: '15',
        textColor: '#6b7280'
      }},
      { id: 'stats-1', type: 'layout-stats', content: {
        stats: [
          { value: '£20', label: 'For You' },
          { value: '£20', label: 'For Them' },
          { value: '∞', label: 'Referrals' }
        ],
        textColor: '#3e4a36'
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        eyebrow: 'How it works',
        heading: 'Three simple steps',
        accentColor: '#75825f',
        textColor: '#3e4a36',
        backgroundColor: '#f4f2ea'
      }},
      { id: 'text-2', type: 'text', content: {
        text: '1. Share your unique referral link below\n2. Your friend books their first appointment\n3. You both receive £20 credit automatically',
        align: 'center',
        fontSize: '15',
        lineHeight: '2',
        textColor: '#3e4a36',
        backgroundColor: '#f4f2ea'
      }},
      { id: 'review-1', type: 'review', content: {
        text: 'I\'ve referred four friends and they\'ve all become regulars! The referral credits basically fund my monthly blowout now',
        author: 'Lauren W.',
        service: 'Referred 4 friends',
        rating: 5,
        backgroundColor: '#f4f2ea'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Start sharing',
        subtext: 'Your unique referral link is ready',
        buttonText: 'Get my referral link',
        buttonUrl: '{{BookingLink}}',
        background: '#3e4a36',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonColor: '#ffffff',
        buttonTextColor: '#3e4a36',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'social-1', type: 'layout-social-strip', content: { style: 'rounded' }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Fresh Cuts — barbershop, bold yellow & maroon ──
  {
    id: 'barbershop-bold',
    name: 'Fresh Cuts',
    description: 'A loud, confident barbershop campaign with bold type, client one-liners, and walk-in energy.',
    category: 'Promotional',
    subject: 'Chairs Are Filling Fast This Week',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'The barbershop',
        rightText: 'Walk-ins welcome',
        textColor: '#571c2f',
        backgroundColor: '#f5dd4f'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'A FRESH CUT\nFIXES\nEVERYTHING',
        subtext: 'Hi {{FirstName}}, chairs are filling fast this week. Book your slot and walk out sharp.',
        backgroundColor: '#f5dd4f',
        textColor: '#571c2f',
        headlineFontFamily: 'Inter, system-ui, sans-serif',
        headlineWeight: '800',
        headlineUppercase: true,
        headlineSize: 48,
        align: 'left'
      }},
      { id: 'shape-1', type: 'layout-shape-image', content: {
        shape: 'arch',
        imageUrl: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600',
        frameColor: '#571c2f',
        backgroundColor: '#f5dd4f',
        width: 70
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        eyebrow: 'The shop, by the numbers',
        heading: 'Why the chair stays busy',
        accentColor: '#8a6a14',
        textColor: '#571c2f',
        backgroundColor: '#f5dd4f',
        headingFontFamily: 'Inter, system-ui, sans-serif',
        headingWeight: '800'
      }},
      { id: 'stats-1', type: 'layout-stats', content: {
        stats: [
          { value: '1,200+', label: 'Cuts This Year' },
          { value: '6', label: 'Chairs' },
          { value: '4.9', label: 'Rating' }
        ],
        textColor: '#571c2f',
        labelColor: '#8a6a14',
        backgroundColor: '#f5dd4f'
      }},
      { id: 'chips-1', type: 'layout-quote-chips', content: {
        q1: '"best fade in town"',
        q2: '"worth every penny"',
        q3: '"the hot towel finish"',
        q4: '"never going anywhere else"',
        chipColor: '#fcf4c4',
        textColor: '#571c2f',
        backgroundColor: '#f5dd4f'
      }},
      { id: 'service-1', type: 'service', content: {
        name: 'Skin Fade & Shape Up',
        description: 'Tight blend, crisp lines, finished with product that suits your hair.',
        price: '28',
        duration: '30 min',
        sectionColor: '#f5dd4f',
        backgroundColor: '#fcf4c4',
        priceColor: '#571c2f'
      }},
      { id: 'service-2', type: 'service', content: {
        name: 'Hot Towel Shave',
        description: 'Old school straight razor shave with hot towels and a cold finish.',
        price: '24',
        duration: '25 min',
        sectionColor: '#f5dd4f',
        backgroundColor: '#fcf4c4',
        priceColor: '#571c2f'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        eyebrow: 'No fuss, just fades',
        headline: 'BOOK YOUR CHAIR',
        subtext: 'Open late Thursdays and Fridays',
        buttonText: 'Book now',
        buttonUrl: '{{BookingLink}}',
        background: '#571c2f',
        headlineWeight: '800',
        buttonColor: '#f5dd4f',
        buttonTextColor: '#571c2f',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: {
        text: '© 2026 {{SalonName}}. All rights reserved.',
        backgroundColor: '#571c2f',
        textColor: '#d9aebc'
      }}
    ]
  },

  // ── Glow Up Sale — bright orange flash promo ──
  {
    id: 'flash-sale-pop',
    name: 'Glow Up Sale',
    description: 'A high-energy flash sale that pops: saturated colour, a flower photo crop, and a countdown.',
    category: 'Promotional',
    subject: '25% Off Everything, This Weekend Only',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'This weekend only',
        rightText: '48 hours',
        textColor: '#5a1b33',
        backgroundColor: '#f2620f'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'THE GLOW\nUP SALE',
        subtext: '25% off every treatment, this weekend only',
        backgroundColor: '#f2620f',
        textColor: '#5a1b33',
        headlineFontFamily: 'Inter, system-ui, sans-serif',
        headlineWeight: '800',
        headlineUppercase: true,
        headlineSize: 52
      }},
      { id: 'shape-1', type: 'layout-shape-image', content: {
        shape: 'flower',
        imageUrl: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600',
        frameColor: '#bcd7f2',
        backgroundColor: '#f2620f',
        width: 72
      }},
      { id: 'text-1', type: 'text', content: {
        text: 'Hi {{FirstName}}, you\'ve been meaning to book that treatment for weeks. This is your sign.',
        align: 'center',
        fontSize: '15',
        textColor: '#ffefe3',
        backgroundColor: '#f2620f'
      }},
      { id: 'promo-1', type: 'layout-promo-banner', content: {
        promoText: 'CODE: GLOW25',
        subtext: '25% off everything until Sunday midnight',
        backgroundColor: '#5a1b33',
        promoTextColor: '#ffffff',
        subtextColor: '#e8b7cb'
      }},
      { id: 'service-1', type: 'service', content: {
        name: 'Hydra-Glow Facial',
        description: 'Deep hydration meets luminous results. Walk out glowing.',
        price: '90',
        duration: '60 min',
        image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=400',
        sectionColor: '#f2620f',
        backgroundColor: '#fbf3dc',
        priceColor: '#c2410c'
      }},
      { id: 'service-2', type: 'service', content: {
        name: 'Gloss & Blow Dry',
        description: 'Mirror shine gloss treatment with a bouncy salon finish.',
        price: '55',
        duration: '45 min',
        sectionColor: '#f2620f',
        backgroundColor: '#fbf3dc',
        priceColor: '#c2410c'
      }},
      { id: 'countdown-1', type: 'layout-countdown', content: {
        headline: 'The sale ends in',
        days: '02',
        hours: '00',
        mins: '00',
        secs: '00',
        backgroundColor: '#5a1b33',
        textColor: '#ffffff'
      }},
      { id: 'btn-1', type: 'button', content: {
        text: 'Claim 25% off',
        url: '{{BookingLink}}',
        shape: 'pill',
        uppercase: true,
        fontSize: 12,
        buttonColor: '#5a1b33',
        textColor: '#ffffff',
        backgroundColor: '#f2620f',
        align: 'center'
      }},
      { id: 'footer-1', type: 'footer', content: {
        text: '© 2026 {{SalonName}}. All rights reserved.',
        backgroundColor: '#5a1b33',
        textColor: '#e8b7cb'
      }}
    ]
  },

  // ── Clinic Fresh — beauty clinic, clean sky blue ──
  {
    id: 'clinic-glow',
    name: 'Clinic Fresh',
    description: 'A calm, credible introduction to clinic treatments with consultation-first messaging.',
    category: 'Newsletter',
    subject: 'New Treatments, Backed by Real Results',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'The skin clinic',
        rightText: 'New treatments',
        textColor: '#14222e',
        backgroundColor: '#ffffff'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'Skin first.\nGlow always.',
        subtext: 'Considered treatments, delivered by practitioners who put skin health before trends.',
        backgroundColor: '#ffffff',
        textColor: '#14222e',
        headlineFontFamily: 'Georgia, serif',
        headlineSize: 44
      }},
      { id: 'shape-1', type: 'layout-shape-image', content: {
        shape: 'arch',
        imageUrl: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600',
        backgroundColor: '#ffffff',
        width: 76
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        eyebrow: 'Consultation first',
        heading: 'Treatments with evidence behind them',
        body: 'Hi {{FirstName}}, every plan starts with a consultation and a proper skin analysis. No guesswork, no pressure.',
        accentColor: '#3e6e91',
        textColor: '#14222e',
        backgroundColor: '#ffffff'
      }},
      { id: 'service-1', type: 'service', content: {
        name: 'Skin Consultation & Analysis',
        description: 'A full assessment of your skin with a personalised treatment plan to take home.',
        price: '25',
        duration: '30 min',
        backgroundColor: '#f2f7fb',
        priceColor: '#3e6e91'
      }},
      { id: 'service-2', type: 'service', content: {
        name: 'Resurfacing Peel',
        description: 'Clinical-grade exfoliation tailored to your skin type, with full aftercare guidance.',
        price: '85',
        duration: '45 min',
        image: 'https://images.unsplash.com/photo-1487412947147-5cebf100ffc2?w=400',
        backgroundColor: '#f2f7fb',
        priceColor: '#3e6e91'
      }},
      { id: 'service-3', type: 'service', content: {
        name: 'LED Light Therapy',
        description: 'Calming, collagen-supporting light treatment. Ideal as a no-downtime add-on.',
        price: '45',
        duration: '20 min',
        backgroundColor: '#f2f7fb',
        priceColor: '#3e6e91'
      }},
      { id: 'duo-1', type: 'layout-duo-grid', content: {
        backgroundColor: '#dce9f4',
        textColor: '#14222e',
        leftImage: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600',
        leftTitle: 'Before your visit',
        leftCaption: 'Arrive makeup free if you can. We take care of the rest.',
        rightImage: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600',
        rightTitle: 'Aftercare included',
        rightCaption: 'Every treatment comes with a written aftercare plan.'
      }},
      { id: 'stats-1', type: 'layout-stats', content: {
        stats: [
          { value: '12+', label: 'Years In Practice' },
          { value: '8,000+', label: 'Treatments Done' },
          { value: '4.9', label: 'Average Rating' }
        ],
        textColor: '#14222e'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Book a consultation',
        subtext: 'Plans tailored to your skin, not the trend',
        buttonText: 'Book now',
        buttonUrl: '{{BookingLink}}',
        background: '#14222e',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonColor: '#ffffff',
        buttonTextColor: '#14222e',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Treat Yourself — playful pink VIP with heart crop ──
  {
    id: 'treat-yourself-vip',
    name: 'Treat Yourself',
    description: 'A playful, feel-good VIP campaign with a heart photo crop and a pamper offer.',
    category: 'VIP',
    subject: 'You\'ve Earned This One, {{FirstName}}',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'You earned this',
        textColor: '#c42847',
        backgroundColor: '#f9cdd6'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'Treat\nyourself.',
        subtext: 'Self-care is not a luxury. This week it\'s 20% off too.',
        backgroundColor: '#f9cdd6',
        textColor: '#c42847',
        headlineFontFamily: 'Georgia, serif',
        headlineItalic: true,
        headlineSize: 50
      }},
      { id: 'shape-1', type: 'layout-shape-image', content: {
        shape: 'heart',
        imageUrl: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=600',
        frameColor: '#ffffff',
        backgroundColor: '#f9cdd6',
        width: 66
      }},
      { id: 'text-1', type: 'text', content: {
        text: 'Hi {{FirstName}}, you book everyone else first. This one is just for you.',
        align: 'center',
        fontSize: '15',
        textColor: '#7a1f33',
        backgroundColor: '#f9cdd6'
      }},
      { id: 'promo-1', type: 'layout-promo-banner', content: {
        promoText: 'CODE: PAMPER20',
        subtext: '20% off your next visit, valid for two weeks',
        backgroundColor: '#c42847',
        promoTextColor: '#ffffff',
        subtextColor: '#f4b8c6'
      }},
      { id: 'service-1', type: 'service', content: {
        name: 'Luxury Wash & Blow Dry',
        description: 'The full ritual: massage wash, conditioning mask, and a flawless finish.',
        price: '48',
        duration: '50 min',
        image: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=400',
        sectionColor: '#f9cdd6',
        backgroundColor: '#fdeef1',
        priceColor: '#c42847'
      }},
      { id: 'service-2', type: 'service', content: {
        name: 'Deluxe Manicure',
        description: 'Shape, cuticle care, massage, and a polish that lasts.',
        price: '38',
        duration: '45 min',
        sectionColor: '#f9cdd6',
        backgroundColor: '#fdeef1',
        priceColor: '#c42847'
      }},
      { id: 'chips-1', type: 'layout-quote-chips', content: {
        q1: '"pure bliss"',
        q2: '"my monthly reset"',
        q3: '"the head massage!!"',
        q4: '"never skipping again"',
        chipColor: '#fdeef1',
        textColor: '#c42847',
        backgroundColor: '#f9cdd6'
      }},
      { id: 'btn-1', type: 'button', content: {
        text: 'Treat yourself',
        url: '{{BookingLink}}',
        shape: 'pill',
        uppercase: true,
        fontSize: 12,
        buttonColor: '#c42847',
        textColor: '#ffffff',
        backgroundColor: '#f9cdd6',
        align: 'center'
      }},
      { id: 'footer-1', type: 'footer', content: {
        text: '© 2026 {{SalonName}}. All rights reserved.',
        backgroundColor: '#c42847',
        textColor: '#f4b8c6'
      }}
    ]
  },

  // ── New Stylist — minimal editorial introduction ──
  {
    id: 'new-stylist',
    name: 'New Team Member',
    description: 'Introduce a new stylist with their story, their specialities, and a way to book them first.',
    category: 'Announcement',
    subject: 'Someone New Behind the Chair',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'New arrival',
        rightText: 'Now taking bookings',
        textColor: '#161616',
        backgroundColor: '#ffffff',
        showRule: true
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'Please welcome\nMaya',
        subtext: 'Hi {{FirstName}}, our newest stylist joins us this month, and her chair is open.',
        backgroundColor: '#ffffff',
        textColor: '#161616',
        headlineFontFamily: 'Georgia, serif',
        headlineSize: 44
      }},
      { id: 'shape-1', type: 'layout-shape-image', content: {
        shape: 'arch',
        imageUrl: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=600',
        backgroundColor: '#ffffff',
        width: 66
      }},
      { id: 'team-1', type: 'team', content: {
        name: 'Maya Collins',
        title: 'Senior Colourist',
        bio: 'Eight years of editorial and salon colour, with a soft spot for lived-in blondes and bold transformations.',
        image: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=400',
        textColor: '#161616',
        accentColor: '#9c9489',
        ringColor: '#e8e4de'
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        eyebrow: 'Her specialities',
        heading: 'What to book her for',
        accentColor: '#9ca3af',
        textColor: '#161616',
        backgroundColor: '#ffffff'
      }},
      { id: 'service-1', type: 'service', content: {
        variant: 'minimal',
        name: 'Lived-in Balayage',
        description: 'Low-maintenance colour that grows out beautifully.',
        price: '160',
        duration: '2.5 hrs',
        currency: '£',
        priceColor: '#161616'
      }},
      { id: 'service-2', type: 'service', content: {
        variant: 'minimal',
        name: 'Gloss & Tone Refresh',
        description: 'A quick colour pick-me-up between appointments.',
        price: '55',
        duration: '45 min',
        currency: '£',
        priceColor: '#161616'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        eyebrow: 'Introductory offer',
        headline: '20% off with Maya this month',
        buttonText: 'Book with Maya',
        buttonUrl: '{{BookingLink}}',
        background: '#161616',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonColor: '#ffffff',
        buttonTextColor: '#161616',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── New Treatment Launch — clinical clean ──
  {
    id: 'new-treatment',
    name: 'New Treatment',
    description: 'Launch a new treatment with the what, the why, and an easy first booking.',
    category: 'Announcement',
    subject: 'New In: A Treatment Worth Trying',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'Just landed',
        rightText: 'On the menu now',
        textColor: '#14222e',
        backgroundColor: '#ffffff',
        showRule: true
      }},
      { id: 'badge-1', type: 'layout-badge', content: {
        badgeText: 'NEW',
        headline: 'The Glass Skin Facial',
        body: 'Hi {{FirstName}}, our newest treatment has arrived: deep hydration layered with gentle resurfacing for a glide-off-the-face glow.',
        badgeColor: '#3e6e91',
        textColor: '#14222e',
        backgroundColor: '#ffffff'
      }},
      { id: 'shape-1', type: 'layout-shape-image', content: {
        shape: 'scallop',
        imageUrl: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600',
        backgroundColor: '#dce9f4',
        width: 68
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        eyebrow: 'Why it works',
        heading: 'Three steps, one glow',
        body: 'Double cleanse and enzyme polish, a hyaluronic infusion, then LED to calm and set. No downtime.',
        accentColor: '#3e6e91',
        textColor: '#14222e',
        backgroundColor: '#ffffff'
      }},
      { id: 'service-1', type: 'service', content: {
        variant: 'stacked',
        name: 'The Glass Skin Facial',
        description: 'A 60 minute reset for dull, dehydrated skin. Launch price for the first month.',
        price: '85',
        duration: '60 min',
        currency: '£',
        image: 'https://images.unsplash.com/photo-1570172619644-dfd03ed5d881?w=600',
        backgroundColor: '#f2f7fb',
        priceColor: '#3e6e91'
      }},
      { id: 'review-1', type: 'review', content: {
        text: 'My skin has honestly never felt like this. Booked my second one before I left.',
        author: 'Elena S.',
        service: 'Glass Skin Facial trial',
        rating: 5,
        backgroundColor: '#f2f7fb'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Be one of the first',
        subtext: 'Launch slots are limited this month',
        buttonText: 'Book the new facial',
        buttonUrl: '{{BookingLink}}',
        background: '#14222e',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonColor: '#ffffff',
        buttonTextColor: '#14222e',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  },

  // ── Gift Card Push — luxe charcoal and gold, real voucher block ──
  {
    id: 'gift-card-push',
    name: 'Gift Card Push',
    description: 'Sell gift vouchers with your real card design front and centre. Perfect before holidays and occasions.',
    category: 'Promotional',
    subject: 'The Gift They Actually Want',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'Gift vouchers',
        rightText: 'Delivered instantly',
        textColor: '#c9a35c',
        backgroundColor: '#232026'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'The gift they\nactually want',
        subtext: 'Hi {{FirstName}}, skip the guesswork. A voucher from us is never the wrong size.',
        backgroundColor: '#232026',
        textColor: '#f8f4ec',
        accentColor: '#c9a35c',
        eyebrow: 'For someone lovely',
        headlineFontFamily: 'Georgia, serif',
        headlineSize: 42
      }},
      { id: 'voucher-1', type: 'voucher', content: {
        name: 'Gift Card',
        variant: 'showcase',
        template: 'signature',
        theme: 'charcoal',
        aesthetic: 'editorial',
        value: '50',
        fromPrice: '25',
        currency: '£',
        expiry: '12 months',
        url: '',
        buttonText: 'Buy a gift voucher',
        salonName: 'Your Salon',
        sectionColor: '#f8f4ec'
      }},
      { id: 'intro-1', type: 'layout-section-intro', content: {
        eyebrow: 'How it works',
        heading: 'Bought in a minute, loved for longer',
        body: 'Choose an amount, add a note, and it lands in their inbox straight away. They book whenever suits them.',
        accentColor: '#a2823f',
        textColor: '#232026',
        backgroundColor: '#f8f4ec'
      }},
      { id: 'chips-1', type: 'layout-quote-chips', content: {
        q1: '"best last-minute save"',
        q2: '"she cried, honestly"',
        q3: '"easiest present ever"',
        q4: '"got one for mum too"',
        chipColor: '#ffffff',
        textColor: '#232026',
        backgroundColor: '#f8f4ec'
      }},
      { id: 'footer-1', type: 'footer', content: {
        text: '© 2026 {{SalonName}}. All rights reserved.',
        backgroundColor: '#232026',
        textColor: '#8d8694'
      }}
    ]
  },

  // ── Birthday Club — bright, fun, personal ──
  {
    id: 'birthday-club',
    name: 'Birthday Treat',
    description: 'A bright, personal birthday email with a treat code. Pairs beautifully with an automated birthday send.',
    category: 'VIP',
    subject: 'Happy Birthday, {{FirstName}}! A Treat Inside',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'It\'s your month',
        textColor: '#7a2847',
        backgroundColor: '#f6b8c8'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'HAPPY\nBIRTHDAY!',
        subtext: 'Hi {{FirstName}}, cake is optional. Great hair is not.',
        backgroundColor: '#f6b8c8',
        textColor: '#7a2847',
        headlineFontFamily: 'Inter, system-ui, sans-serif',
        headlineWeight: '800',
        headlineUppercase: true,
        headlineSize: 52
      }},
      { id: 'shape-1', type: 'layout-shape-image', content: {
        shape: 'heart',
        imageUrl: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=600',
        frameColor: '#ffffff',
        backgroundColor: '#f6b8c8',
        width: 64
      }},
      { id: 'promo-1', type: 'layout-promo-banner', content: {
        promoText: 'Your birthday treat',
        subtext: '20% off any treatment during your birthday month',
        backgroundColor: '#7a2847',
        promoTextColor: '#ffffff',
        subtextColor: '#f6b8c8'
      }},
      { id: 'chips-1', type: 'layout-quote-chips', content: {
        q1: '"birthday blowout!"',
        q2: '"new year, new hair"',
        q3: '"treat yo self"',
        q4: '"best birthday tradition"',
        chipColor: '#ffffff',
        textColor: '#7a2847',
        backgroundColor: '#f6b8c8'
      }},
      { id: 'btn-1', type: 'button', content: {
        text: 'Claim your birthday treat',
        url: '{{BookingLink}}',
        shape: 'pill',
        uppercase: true,
        fontSize: 12,
        buttonColor: '#7a2847',
        textColor: '#ffffff',
        backgroundColor: '#f6b8c8',
        align: 'center'
      }},
      { id: 'footer-1', type: 'footer', content: {
        text: '© 2026 {{SalonName}}. All rights reserved.',
        backgroundColor: '#7a2847',
        textColor: '#dba3b6'
      }}
    ]
  },

  // ── VIP Early Access — dark luxe minimal ──
  {
    id: 'vip-preview',
    name: 'VIP First Access',
    description: 'Give your best clients first pick of new dates, treatments, or seasonal slots. Quiet luxury, no shouting.',
    category: 'VIP',
    subject: 'Before Anyone Else, {{FirstName}}',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'For our regulars',
        rightText: '48 hours early',
        textColor: '#e8a838',
        backgroundColor: '#1a1a1e'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        eyebrow: 'First access',
        headline: 'You see this\nbefore anyone',
        subtext: 'Hi {{FirstName}}, our December diary opens to everyone on Friday. For you, it opens now.',
        backgroundColor: '#1a1a1e',
        textColor: '#ffffff',
        accentColor: '#e8a838',
        headlineFontFamily: 'Georgia, serif',
        headlineSize: 42
      }},
      { id: 'service-1', type: 'service', content: {
        variant: 'minimal',
        name: 'Signature cut & finish',
        description: 'Saturday mornings go first, every single time.',
        price: '60',
        duration: '60 min',
        currency: '£',
        sectionColor: '#ffffff',
        priceColor: '#1a1a1e'
      }},
      { id: 'service-2', type: 'service', content: {
        variant: 'minimal',
        name: 'Party season blow dries',
        description: 'Book the run-up to the holidays before the rush.',
        price: '45',
        duration: '45 min',
        currency: '£',
        sectionColor: '#ffffff',
        priceColor: '#1a1a1e'
      }},
      { id: 'countdown-1', type: 'layout-countdown', content: {
        headline: 'Early access closes in',
        days: '02',
        hours: '00',
        mins: '00',
        secs: '00',
        backgroundColor: '#1a1a1e',
        textColor: '#ffffff'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Take your pick',
        subtext: 'Quietly, before Friday',
        buttonText: 'Browse early slots',
        buttonUrl: '{{BookingLink}}',
        background: '#1a1a1e',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonVariant: 'outline',
        buttonColor: '#e8a838',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: {
        text: '© 2026 {{SalonName}}. All rights reserved.',
        backgroundColor: '#1a1a1e',
        textColor: '#6f6a76'
      }}
    ]
  },

  // ── We Miss You — soft sage win-back ──
  {
    id: 'we-miss-you',
    name: 'We Miss You',
    description: 'A warm win-back for clients who have not visited in a while, with a gentle reason to return.',
    category: 'Promotional',
    subject: 'It\'s Been a While, {{FirstName}}',
    blocks: [
      { id: 'bar-1', type: 'layout-utility-bar', content: {
        leftText: 'Your chair misses you',
        textColor: '#75825f',
        backgroundColor: '#f4f2ea'
      }},
      { id: 'hero-1', type: 'layout-editorial-hero', content: {
        headline: 'It\'s been\na while',
        subtext: 'Hi {{FirstName}}, no guilt, just good hair. We would love to see you back in the chair.',
        backgroundColor: '#f4f2ea',
        textColor: '#3e4a36',
        headlineFontFamily: 'Georgia, serif',
        headlineItalic: true,
        headlineSize: 44
      }},
      { id: 'duo-1', type: 'layout-duo-grid', content: {
        backgroundColor: '#f4f2ea',
        textColor: '#3e4a36',
        leftImage: 'https://images.unsplash.com/photo-1522337094846-8a818192de1f?w=600',
        leftTitle: 'What\'s new',
        leftCaption: 'New faces, new treatments, same place you know.',
        rightImage: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=600',
        rightTitle: 'Same chair',
        rightCaption: 'Your usual is still our favourite thing to do.'
      }},
      { id: 'promo-1', type: 'layout-promo-banner', content: {
        promoText: 'WELCOME BACK, 20% OFF',
        subtext: 'Any service on your next visit, on us to say hello again',
        backgroundColor: '#75825f',
        promoTextColor: '#ffffff',
        subtextColor: '#e3e8d8'
      }},
      { id: 'cta-1', type: 'layout-cta-gradient', content: {
        headline: 'Pick up where we left off',
        subtext: 'Your favourite slot is probably free right now',
        buttonText: 'Rebook in seconds',
        buttonUrl: '{{BookingLink}}',
        background: '#3e4a36',
        fontFamily: 'Georgia, serif',
        headlineWeight: '400',
        buttonColor: '#ffffff',
        buttonTextColor: '#3e4a36',
        buttonShape: 'pill',
        buttonUppercase: true
      }},
      { id: 'footer-1', type: 'footer', content: { text: '© 2026 {{SalonName}}. All rights reserved.' }}
    ]
  }
]

// Template categories for filtering
export const templateCategories = [
  { id: 'all', label: 'All Templates' },
  { id: 'promotional', label: 'Promotional' },
  { id: 'newsletter', label: 'Newsletter' },
  { id: 'announcement', label: 'Announcement' },
  { id: 'vip', label: 'VIP' }
]
